import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Avatar, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Modal, 
  TextField,
  Divider,
  Fade,
  Stack
} from "@mui/material";
import { 
  Users, 
  History, 
  Wallet, 
  BarChart3, 
  MessageSquare, 
  Settings2, 
  Mail,
  ChevronRight,
  TrendingUp
} from "lucide-react";
import Reporte from "../Coordinador/Reporte";

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  azur: "#28708B",
  dark: "#22404D",
  pearl: "#EEF3F4",
  red: "#F65F4E"
};

const DashboardPrograma = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [programa, setPrograma] = useState({});
  const [coordinadores, setCoordinadores] = useState([]);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [presupuesto, setPresupuesto] = useState("");
  const [actualizar, setActualizar] = useState(0);

  const rol = localStorage.getItem("rol");
  const isAuditoria = rol === "auditoria";

  useEffect(() => {
    const fetchPrograma = async () => {
      try {
        const response = await fetch(`${apiUrl}/Programa/${id}`, {
          headers: { 'apiKey': localStorage.getItem("apiKey") }
        });
        if (response.ok) setPrograma(await response.json());
      } catch (error) { console.error(error); }
    };
    fetchPrograma();
  }, [id, apiUrl]);

  useEffect(() => {
    const fetchCoordinadores = async () => {
      try {
        const response = await fetch(`${apiUrl}/Programa/obtenerCoordinadores?id=${id}`, {
          headers: { 'apiKey': localStorage.getItem("apiKey") }
        });
        if (response.ok) setCoordinadores(await response.json());
      } catch (error) { console.error(error); }
    };
    fetchCoordinadores();
  }, [id, apiUrl]);

  const hacerPresupuesto = async () => {
    const response = await fetch(`${apiUrl}/Programa/Prosupuesto?idPrograma=${programa.id}&Monto=${presupuesto}`, {
      method: 'POST',
      headers: { 'apiKey': localStorage.getItem("apiKey") }
    });
    if (response.ok) {
      setActualizar(prev => prev + 1);
      setMostrarModal(false);
      setPresupuesto("");
    }
  };

  const navItems = [
    { label: "Historial Caja Chica", icon: <Wallet size={20} />, action: () => navigate(`/listadoGastosPorPrograma/${id}`), hide: isAuditoria },
    { label: "Historial de Adelantos", icon: <History size={20} />, action: () => navigate(`/adelantos/${id}`), hide: isAuditoria },
    { label: "Historial Total", icon: <BarChart3 size={20} />, action: () => navigate(`/listadoGastosPorPrograma/${id}`), hide: isAuditoria },
    { label: "Pedidos Comunicación", icon: <MessageSquare size={20} />, action: () => navigate(`/listaPedidosPorPrograma/${id}`), hide: !isAuditoria },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 6 }, pt: { xs: 10, md: 14 }, bgcolor: "#FFFFFF", minHeight: "100vh" }}>
      <Grid container spacing={4}>
        
        {/* SIDEBAR: INFO & ACCIONES */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 4, borderRadius: '32px', bgcolor: PALETTE.pearl, border: `1px solid ${PALETTE.sky}`, position: 'sticky', top: '120px' }}>
            
            {/* LOGO */}
            {programa.logo && (
              <Box sx={{ textAlign: 'center', mb: 4, p: 2, bgcolor: 'white', borderRadius: '24px', border: `1px solid ${PALETTE.ice}` }}>
                <img
                  src={`data:${programa.logoMimeType};base64,${programa.logo}`}
                  alt="Logo Programa"
                  style={{ maxWidth: "100%", maxHeight: "120px", objectFit: 'contain' }}
                />
              </Box>
            )}

            <Typography variant="h6" sx={{ fontWeight: 900, color: PALETTE.dark, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Users size={20} color={PALETTE.teal} /> Coordinadores
            </Typography>

            <List sx={{ mb: 4 }}>
              {coordinadores.length > 0 ? coordinadores.map((coord) => (
                <ListItem key={coord.id} disableGutters sx={{ mb: 1 }}>
                  <Avatar sx={{ bgcolor: PALETTE.ice, color: PALETTE.azur, mr: 2 }}>{coord.nombre[0]}</Avatar>
                  <ListItemText 
                    primary={<Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: PALETTE.dark }}>{coord.nombre} {coord.apellido}</Typography>}
                    secondary={<Typography sx={{ fontSize: '0.75rem', color: PALETTE.azur, display: 'flex', alignItems: 'center', gap: 0.5 }}><Mail size={12}/> {coord.email}</Typography>}
                  />
                </ListItem>
              )) : (
                <Typography variant="caption" color="text.secondary">No hay coordinadores asignados.</Typography>
              )}
            </List>

            <Divider sx={{ mb: 3 }} />

            <Stack spacing={1.5}>
              {navItems.filter(item => !item.hide).map((item, index) => (
                <Button
                  key={index}
                  variant="contained"
                  fullWidth
                  onClick={item.action}
                  startIcon={item.icon}
                  endIcon={<ChevronRight size={16} />}
                  sx={{ 
                    justifyContent: 'space-between', borderRadius: '14px', textTransform: 'none', fontWeight: 700,
                    bgcolor: 'white', color: PALETTE.dark, boxShadow: 'none', border: '1px solid transparent',
                    '&:hover': { bgcolor: PALETTE.ice, border: `1px solid ${PALETTE.sky}`, color: PALETTE.teal, boxShadow: 'none' }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* MAIN CONTENT: REPORTES */}
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <Box>
              <Typography variant="overline" sx={{ color: PALETTE.teal, fontWeight: 800, letterSpacing: 1.5 }}>PANEL DE CONTROL</Typography>
              <Typography variant="h3" sx={{ fontWeight: 900, color: PALETTE.dark, lineHeight: 1 }}>{programa.nombre || "Programa"}</Typography>
            </Box>
            
            {!isAuditoria && (
              <Button 
                variant="outlined" 
                startIcon={<Settings2 size={18} />}
                onClick={() => setMostrarModal(true)}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800, borderColor: PALETTE.sky, color: PALETTE.azur }}
              >
                Presupuesto
              </Button>
            )}
          </Box>

          <Paper elevation={0} sx={{ p: 1, borderRadius: '32px', border: `1px solid #eee` }}>
             <Reporte actualizar={actualizar} idPrograma={id} />
          </Paper>
        </Grid>

      </Grid>

      {/* MODAL DE PRESUPUESTO ESTILO SOFT */}
      <Modal open={mostrarModal} onClose={() => setMostrarModal(false)} closeAfterTransition>
        <Fade in={mostrarModal}>
          <Box sx={{ 
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 400, bgcolor: 'background.paper', borderRadius: '28px', boxShadow: '0 20px 60px rgba(0,0,0,0.1)', p: 4 
          }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Avatar sx={{ bgcolor: PALETTE.ice, color: PALETTE.teal, margin: '0 auto 16px', width: 56, height: 56 }}>
                <TrendingUp size={30} />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 900, color: PALETTE.dark }}>Actualizar Presupuesto</Typography>
              <Typography variant="body2" sx={{ color: PALETTE.textGray }}>Define el nuevo monto límite para el programa.</Typography>
            </Box>

            <TextField
              fullWidth
              type="number"
              label="Monto del Presupuesto"
              value={presupuesto}
              onChange={(e) => setPresupuesto(e.target.value)}
              placeholder="Ej: 50000"
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, fontWeight: 700, color: PALETTE.teal }}>$</Typography> }}
              sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
            />

            <Stack direction="row" spacing={2}>
              <Button fullWidth onClick={() => setMostrarModal(false)} sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, color: PALETTE.textGray }}>
                Cancelar
              </Button>
              <Button 
                fullWidth variant="contained" onClick={hacerPresupuesto}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, bgcolor: PALETTE.dark, '&:hover': { bgcolor: PALETTE.teal } }}
              >
                Guardar Cambios
              </Button>
            </Stack>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default DashboardPrograma;